/*
 * ModelPortfolioPanel
 * ───────────────────
 * The "Model Portfolio" view for a deployed StrategyPortfolio. Renders inline in
 * the Strategies → Portfolio Deployments expanded panel, beneath the backtest
 * analytics.
 *
 * What it shows: the portfolio's allocation measured in PERCENTAGE weights per
 * security — capital-base independent. Three weight columns:
 *   • Start (①)        — the DESIGN/CONTROL weight (frozen design weight × current
 *                        target security). Price-free & drift-immune; drives orders.
 *   • Model target (③) — ① marked-to-market by each name's price drift since entry
 *                        (latest close ÷ entry). On entry day ③ == ①. This is what
 *                        the book SHOULD be worth today if it tracked the design.
 *   • Actual (②)       — held shares × last close per security over the portfolio's
 *                        TOTAL equity (same base as ③) with a CASH bucket for the
 *                        un-invested remainder. INFORMATIONAL ONLY; never feeds orders.
 * and two drifts:
 *   • Model drift = ③ − ① — price drift's effect alone (no execution).
 *   • Drift       = ② − ③ — the EXECUTION gap (held book vs live target).
 *
 * IBKR is not touched here and no order is placed: this is read-only compute used
 * to drive a much larger book by hand via Bloomberg (Phase 2 adds the share /
 * incremental-order generators + CSV/Excel export).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { tradingService } from '../../services/tradingService';

const num = (v) => (v == null || Number.isNaN(Number(v)) ? null : Number(v));
const fmtPct = (v, d = 2) => {
  const n = num(v);
  return n == null ? '—' : `${(n * 100).toFixed(d)}%`;
};
const fmtSignedPct = (v, d = 2) => {
  const n = num(v);
  if (n == null) return '—';
  const s = `${(n * 100).toFixed(d)}%`;
  return n > 0 ? `+${s}` : s;
};
const signed = (v) => {
  const n = num(v);
  if (n == null) return 'text-gray-400';
  return n > 0 ? 'text-green-600 dark:text-green-400'
       : n < 0 ? 'text-red-600 dark:text-red-400'
       : 'text-gray-500';
};

const ModelPortfolioPanel = ({ portfolioId }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');
  const [showPending, setShowPending] = useState(false);

  const fetchOnce = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await tradingService.getModelWeights(portfolioId);
      setData(res.data);
    } catch (e) {
      setErr('Could not load model weights.');
    } finally {
      setLoading(false);
    }
  }, [portfolioId]);

  useEffect(() => { fetchOnce(); }, [fetchOnce]);

  const rows = data?.rows || [];
  const startTotal = rows.reduce((s, r) => s + (num(r.start_weight) || 0), 0);
  const modelTotal = rows.reduce((s, r) => s + (num(r.model_implied_weight) || 0), 0);

  const pending = data?.pending || [];
  const subShareCount = pending.filter((r) => r.reason === 'sub_share').length;
  const flatCount = pending.filter((r) => r.reason === 'flat').length;

  return (
    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Model Portfolio · Net weights per security
        </h4>
        <button
          onClick={fetchOnce}
          disabled={loading}
          className="text-[10px] text-brand-gold hover:underline disabled:opacity-40"
          title="Recompute net weights"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <p className="text-[10px] text-gray-400 dark:text-gray-500 mb-2 leading-relaxed">
        Capital-base independent. <span className="font-medium">Start</span> is the design/control
        allocation that drives orders (price-free). <span className="font-medium">Model target</span> is
        Start marked-to-market by price drift since entry — the live target. <span className="font-medium">Actual</span> is
        the market-value share of current holdings (informational only). <span className="font-medium">Drift</span> = Actual
        − Model target is the execution gap to act on.
        {data?.as_of ? <span className="text-gray-400"> · as of {data.as_of}</span> : null}
      </p>

      {err ? (
        <p className="text-xs text-red-500 py-2">{err}</p>
      ) : loading ? (
        <p className="text-xs text-gray-500 py-2">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-gray-500 py-2">No weights to display.</p>
      ) : (
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-100 dark:border-gray-800">
                <th className="py-1.5 pr-3 font-medium">Security</th>
                <th className="py-1.5 pr-3 font-medium text-right">Start</th>
                <th className="py-1.5 pr-3 font-medium text-right">Model target</th>
                <th className="py-1.5 pr-3 font-medium text-right">Model drift</th>
                <th className="py-1.5 pr-3 font-medium text-right">Actual</th>
                <th className="py-1.5 pr-3 font-medium text-right">Drift</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.symbol} className="border-b border-gray-50 dark:border-gray-800/50">
                  <td className="py-1.5 pr-3 font-mono font-medium text-gray-700 dark:text-gray-200">
                    {r.symbol}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-gray-700 dark:text-gray-300">
                    {fmtPct(r.start_weight)}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-gray-700 dark:text-gray-300">
                    {fmtPct(r.model_implied_weight)}
                  </td>
                  <td className={`py-1.5 pr-3 text-right tabular-nums ${signed(r.model_drift)}`}>
                    {r.model_drift == null ? '—' : fmtSignedPct(r.model_drift)}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-gray-500 dark:text-gray-400">
                    {r.actual_weight == null ? '—' : fmtPct(r.actual_weight)}
                  </td>
                  <td className={`py-1.5 pr-3 text-right tabular-nums ${signed(r.drift)}`}>
                    {r.drift == null ? '—' : fmtSignedPct(r.drift)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200 dark:border-gray-700 font-medium">
                <td className="py-1.5 pr-3 text-gray-500 uppercase text-[9px] tracking-wider">Total</td>
                <td className="py-1.5 pr-3 text-right tabular-nums text-gray-700 dark:text-gray-300">
                  {fmtPct(startTotal)}
                </td>
                <td className="py-1.5 pr-3 text-right tabular-nums text-gray-700 dark:text-gray-300">
                  {fmtPct(modelTotal)}
                </td>
                <td className="py-1.5 pr-3" />
                <td className="py-1.5 pr-3" />
                <td className="py-1.5 pr-3" />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {!loading && !err && pending.length > 0 ? (
        <div className="mt-3 pt-3 border-t border-dashed border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setShowPending((v) => !v)}
            className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 hover:text-brand-gold"
            title="Deployable names not currently held as a whole share"
          >
            <span>{showPending ? '▾' : '▸'}</span>
            Pending sleeves · {pending.length}
            <span className="font-normal normal-case tracking-normal text-gray-400">
              ({subShareCount} sub-share → cash, {flatCount} flat → idle)
            </span>
          </button>
          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 leading-relaxed">
            Deployable model names holding &lt; 1 whole share right now, so their capital
            is parked. This is the part of the netted SPY / CASH weight that is parked
            capital — not a deliberate SPY/cash bet.
          </p>

          {showPending ? (
            <div className="overflow-x-auto scrollbar-thin mt-2">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-100 dark:border-gray-800">
                    <th className="py-1 pr-3 font-medium">Security</th>
                    <th className="py-1 pr-3 font-medium text-right">Design weight</th>
                    <th className="py-1 pr-3 font-medium text-right">Held</th>
                    <th className="py-1 pr-3 font-medium text-right">Target</th>
                    <th className="py-1 pr-3 font-medium">Reason</th>
                    <th className="py-1 pr-3 font-medium">Parked in</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((r) => (
                    <tr key={r.symbol} className="border-b border-gray-50 dark:border-gray-800/50">
                      <td className="py-1 pr-3 font-mono font-medium text-gray-700 dark:text-gray-200">
                        {r.symbol}
                      </td>
                      <td className="py-1 pr-3 text-right tabular-nums text-gray-700 dark:text-gray-300">
                        {fmtPct(r.design_weight)}
                      </td>
                      <td className="py-1 pr-3 text-right tabular-nums text-gray-500 dark:text-gray-400">
                        {num(r.held_qty) == null ? '—' : Number(r.held_qty).toFixed(3)}
                      </td>
                      <td className="py-1 pr-3 text-right tabular-nums text-gray-500 dark:text-gray-400">
                        {num(r.target_qty) == null ? '—' : Number(r.target_qty).toFixed(3)}
                      </td>
                      <td className="py-1 pr-3">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider ${
                          r.reason === 'sub_share'
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                            : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                        }`}>
                          {r.reason === 'sub_share' ? 'sub-share' : 'flat'}
                        </span>
                      </td>
                      <td className="py-1 pr-3 font-mono text-gray-500 dark:text-gray-400">
                        {r.parked_in}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default ModelPortfolioPanel;
